import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, getAccessToken } from '../lib/api';
import { compressImage, uploadImage } from '../lib/upload';
import { InvoiceQrScanner } from '../components/InvoiceQrScanner';

type Step = 'capture' | 'analyzing' | 'confirm' | 'success';

type AnalysisResult = {
  hasInvoice: boolean;
  invoiceIndex: number | null;
  invoiceNumber: string | null;
  products: { name: string; photo_index: number; invoice_quantity: number | null }[];
  labelsCount: number;
  error: string | null;
};

type DispatchStopInfo = {
  invnum: string;
  shipDate: string;
  cusnum: string | null;
  customerName: string | null;
  routeCode: string | null;
  stopNum: number | null;
  truckCode: string | null;
  invoiceTotal: number | null;
};

type InvoiceLookup = {
  found: boolean;
  stop?: DispatchStopInfo;
  store?: { id: string; name: string } | null;
};

type ScannedInvoice = {
  invnum: string;
  found: boolean;
  dispatch?: DispatchStopInfo;
  storeMatch?: { id: string; name: string } | null;
};

export function NewRequestPage() {
  const { me } = useAuth();
  const nav = useNavigate();

  const storeId = me?.stores[0]?.id;
  const storeCusnum = me?.stores[0]?.cusnum ?? null;
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<{ url: string; preview: string }[]>([]);
  const [step, setStep] = useState<Step>('capture');
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanned, setScanned] = useState<ScannedInvoice | null>(null);
  const [creditType, setCreditType] = useState<'immediate' | 'open'>('immediate');
  const [boxes, setBoxes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (sessionId || !me) return;
    void (async () => {
      try {
        const s = await api<{ id: string }>('/api/v1/submissions/start', {
          method: 'POST',
          body: storeId ? { storeId } : {},
        });
        setSessionId(s.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión de subida');
      }
    })();
  }, [sessionId, me, storeId]);

  if (!me) return null;
  if (!storeId) return <div className="p-6 text-rose-700 text-lg">No tienes tienda asignada. Contacta al admin.</div>;

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || !sessionId) return;
    setError(null);
    setBusy(true);
    const total = files.length;
    setProgress({ done: 0, total });
    try {
      for (let i = 0; i < total; i++) {
        const file = files[i];
        if (!file) continue;
        const compressed = await compressImage(file).catch(() => file);
        const preview = URL.createObjectURL(compressed);
        const res = (await uploadImage(
          `/api/v1/submissions/${sessionId}/photos`,
          compressed,
          file.name.replace(/\.[^.]+$/, '.jpg'),
          getAccessToken(),
        )) as { url: string; count: number };
        setPhotos((prev) => [...prev, { url: res.url, preview }]);
        setProgress({ done: i + 1, total });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir');
    } finally {
      setBusy(false);
      setProgress(null);
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const onScanned = async (invnum: string) => {
    setScannerOpen(false);
    try {
      const r = await api<InvoiceLookup>(`/api/v1/dispatch/invoice/${encodeURIComponent(invnum)}`);
      setScanned({ invnum, found: r.found, dispatch: r.stop, storeMatch: r.store ?? null });
    } catch {
      // El lookup es informativo: sin despacho igual se acepta el número escaneado.
      setScanned({ invnum, found: false });
    }
  };

  // Factura de otra tienda: la parada del despacho tiene cusnum y difiere del
  // cusnum de la tienda del PM. Advertimos pero NO bloqueamos.
  const cusnumMismatch =
    !!scanned?.dispatch?.cusnum && !!storeCusnum && scanned.dispatch.cusnum !== storeCusnum;

  const onSubmit = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    setStep('analyzing');
    try {
      const r = await api<{ analysisResults: AnalysisResult }>(
        `/api/v1/submissions/${sessionId}/submit`,
        { method: 'POST' },
      );
      setAnalysis(r.analysisResults);
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error en análisis');
      setStep('capture');
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { storeId, creditType };
      if (creditType === 'immediate' && boxes) body.boxesAffected = Number(boxes);
      if (scanned) body.scannedInvoiceNumber = scanned.invnum;
      const r = await api<{ creditRequestId: string; claimNumber: string }>(
        `/api/v1/submissions/${sessionId}/confirm`,
        { method: 'POST', body },
      );
      setStep('success');
      setTimeout(() => nav(`/request/${r.creditRequestId}`), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo confirmar');
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async () => {
    if (sessionId) {
      try { await api(`/api/v1/submissions/${sessionId}/cancel`, { method: 'POST' }); } catch {}
    }
    nav('/');
  };

  return (
    <div className="space-y-5 max-w-md mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Nueva solicitud</h1>
        <button onClick={onCancel} className="text-base text-slate-500 hover:text-slate-900 px-2 py-1">
          cancelar
        </button>
      </div>

      {step === 'capture' && (
        <>
          <div className="bg-brand-50 border-2 border-brand-200 rounded-2xl p-5 text-base text-brand-900">
            <p className="font-semibold text-lg">Necesito 3+ fotos:</p>
            <ul className="text-base mt-2 list-disc pl-6 space-y-1">
              <li>📄 Factura (con número visible)</li>
              <li>📦 Producto dañado (mínimo 1)</li>
              <li>🏷️ Etiqueta de caja/lote (mínimo 1)</li>
            </ul>
          </div>

          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="aspect-square bg-slate-100 rounded-2xl overflow-hidden ring-2 ring-emerald-200">
                  <img src={p.preview} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
              className="py-5 bg-white border-2 border-dashed border-brand-400 rounded-2xl flex flex-col items-center justify-center text-brand-700 hover:bg-brand-50 active:bg-brand-100 transition disabled:opacity-50"
            >
              <span className="text-4xl">📸</span>
              <span className="text-base mt-1.5 font-semibold">Cámara</span>
            </button>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              disabled={busy}
              className="py-5 bg-white border-2 border-dashed border-brand-400 rounded-2xl flex flex-col items-center justify-center text-brand-700 hover:bg-brand-50 active:bg-brand-100 transition disabled:opacity-50"
            >
              <span className="text-4xl">🖼️</span>
              <span className="text-base mt-1.5 font-semibold">Galería</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            disabled={busy}
            className="w-full py-3.5 bg-white border-2 border-brand-400 rounded-2xl flex items-center justify-center gap-2 text-brand-700 text-base font-semibold hover:bg-brand-50 active:bg-brand-100 transition disabled:opacity-50"
          >
            <span className="text-2xl">📷</span> Escanear QR de la factura
          </button>

          {scanned && (
            <div className="space-y-2">
              <div
                className={`flex items-center justify-between gap-2 rounded-xl border-2 px-4 py-3 text-base font-medium ${
                  scanned.found
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}
              >
                <span>
                  {scanned.found
                    ? `Factura ${scanned.invnum}${scanned.dispatch?.routeCode ? ` · Ruta ${scanned.dispatch.routeCode}` : ''} · verificada ✓`
                    : `Factura ${scanned.invnum} · no encontrada en despacho — se enviará igual`}
                </span>
                <button
                  type="button"
                  onClick={() => setScanned(null)}
                  aria-label="Quitar factura escaneada"
                  className="shrink-0 w-7 h-7 rounded-full bg-white/70 text-slate-500 hover:text-slate-800 font-bold"
                >
                  ✕
                </button>
              </div>
              {cusnumMismatch && (
                <p className="text-base text-amber-900 bg-amber-100 border-2 border-amber-300 rounded-xl px-4 py-3 font-semibold">
                  ⚠ Esta factura es de otra tienda: {scanned.dispatch?.customerName ?? scanned.storeMatch?.name ?? scanned.dispatch?.cusnum}
                </p>
              )}
            </div>
          )}

          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPickFiles(e.target.files)}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => onPickFiles(e.target.files)}
          />

          {progress && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-base text-sky-900 text-center">
              ⏳ Subiendo {progress.done}/{progress.total}…
            </div>
          )}

          {error && <ErrorBox text={error} />}

          <div className="flex justify-between items-center pt-2">
            <span className="text-base text-slate-600 font-medium">{photos.length}/3+ fotos</span>
            <button
              onClick={onSubmit}
              disabled={busy || photos.length < 3}
              className="px-6 py-3.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-lg font-bold rounded-full shadow-md"
            >
              Enviar a análisis
            </button>
          </div>
        </>
      )}

      {step === 'analyzing' && (
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-10 text-center">
          <div className="text-6xl animate-pulse">🔍</div>
          <p className="mt-4 text-xl text-slate-800 font-semibold">Analizando fotos con IA…</p>
          <p className="text-base text-slate-500 mt-2">Esto puede tardar 10-20 segundos.</p>
        </div>
      )}

      {step === 'confirm' && analysis && (
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-5 space-y-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900">¿Confirmar análisis?</h2>
            <ul className="text-base text-slate-700 mt-3 space-y-1.5">
              <li>
                📄 Factura:{' '}
                <span className="font-mono font-semibold">{scanned?.invnum ?? analysis.invoiceNumber ?? '—'}</span>
                {scanned && <span className="ml-2 text-sm font-semibold text-emerald-700">QR ✓</span>}
              </li>
              <li>📦 Productos: {analysis.products.map((p) => p.name).join(', ') || '—'}</li>
              <li>🏷️ Etiquetas detectadas: {analysis.labelsCount}</li>
            </ul>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-800 mb-2">Tipo de crédito</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setCreditType('immediate')}
                className={`px-4 py-3.5 rounded-xl text-base font-semibold transition ${creditType === 'immediate' ? 'bg-brand-600 text-white shadow' : 'bg-slate-100 text-slate-700'}`}
              >
                ❌ Está mala
              </button>
              <button
                onClick={() => setCreditType('open')}
                className={`px-4 py-3.5 rounded-xl text-base font-semibold transition ${creditType === 'open' ? 'bg-brand-600 text-white shadow' : 'bg-slate-100 text-slate-700'}`}
              >
                📦 Voy a trabajarla
              </button>
            </div>
          </div>

          {creditType === 'immediate' && (
            <label className="block">
              <span className="text-base text-slate-700 font-medium">Cajas afectadas</span>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={boxes}
                onChange={(e) => setBoxes(e.target.value)}
                placeholder="Ej. 3"
                className="w-full mt-2 px-4 py-3.5 text-lg border-2 border-slate-300 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
              />
            </label>
          )}

          {error && <ErrorBox text={error} />}

          <div className="flex gap-3 pt-1">
            <button
              onClick={() => setStep('capture')}
              className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold"
            >
              Atrás
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              className="flex-1 px-4 py-3.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-base font-bold rounded-full shadow-md"
            >
              {busy ? '…' : 'Crear crédito'}
            </button>
          </div>
        </div>
      )}

      {scannerOpen && <InvoiceQrScanner onScan={(inv) => void onScanned(inv)} onClose={() => setScannerOpen(false)} />}

      {step === 'success' && (
        <div className="bg-white rounded-2xl shadow-md border-2 border-emerald-200 p-10 text-center">
          <div className="text-6xl">✅</div>
          <p className="mt-4 text-2xl text-emerald-700 font-bold">Crédito creado</p>
          <p className="text-base text-slate-500 mt-2">Te llevamos al detalle…</p>
        </div>
      )}
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-4 font-medium">{text}</p>;
}
